import { Checkbox } from './Checkbox';
import { ColorCell } from './cells/ColorCell';
import { ImgCell } from './cells/ImgCell';
import { FormsCell } from './cells/FormsCell';
import type { Forms } from './cells/FormsCell';
import { WebsiteCell } from './cells/WebsiteCell';
import { RedirectCell } from './cells/RedirectCell';
import { PagesCell } from './cells/PagesCell';
import { FaviconCell } from './cells/FaviconCell';
import { TagsCell } from './cells/TagsCell';
import { DefaultCell } from './cells/DefaultCell';
import { Highlight } from './Highlight';
import { fromCamelCaseToWords } from '../misc/functions';
import { NO_DATA } from '../misc/misc.constants';
import { COLUMNS } from '../misc/columns.constants';
import type { Filters, Website } from '../types';

export interface TableRowProps {
  websiteData: Website;
  showColumns: string[];
  host: string;
  websiteIndex: number;
  filters: Filters;
  checkboxes: number[];
  onCheckboxChange: (index: number) => void;
}

export function TableRow({
  websiteData,
  showColumns,
  host,
  websiteIndex,
  filters,
  checkboxes,
  onCheckboxChange,
}: TableRowProps) {
  return (
    <tr>
      {showColumns.map((column) => {
        switch (column) {
          case COLUMNS.index:
            return (
              <td
                data-title={fromCamelCaseToWords(column)}
                data-qa={column}
                key={column}
              >
                {websiteIndex}
              </td>
            );

          case COLUMNS.checkbox:
            return (
              <td
                data-title={fromCamelCaseToWords(column)}
                data-qa={column}
                key={column}
              >
                <Checkbox
                  name={''}
                  label={''}
                  checked={checkboxes.includes(websiteIndex)}
                  onChange={() => onCheckboxChange(websiteIndex)}
                />
              </td>
            );

          case COLUMNS.website:
            return (
              <WebsiteCell
                key={column}
                column={column}
                websiteData={websiteData}
                host={host}
                filters={filters}
              />
            );

          case COLUMNS.ocsDefaultRedirect:
          case COLUMNS.rootRedirect:
            return (
              <RedirectCell
                key={column}
                column={column}
                websiteData={websiteData}
                filters={filters}
              />
            );

          case COLUMNS.ogImage: {
            const images = (websiteData[column] as string[]).map(
              (path) => `https://${host}/${path}`,
            );
            return (
              <td
                data-title={fromCamelCaseToWords(column)}
                data-qa={column}
                key={column}
              >
                {images.length ? <ImgCell sources={images} /> : NO_DATA}
              </td>
            );
          }

          case COLUMNS.favicon:
            return (
              <FaviconCell
                key={column}
                column={column}
                websiteData={websiteData}
                host={host}
              />
            );

          case COLUMNS.mainFormTheme:
          case COLUMNS.altFormTheme:
          case COLUMNS.mainFormEsTheme: {
            const bgColorKey = {
              [COLUMNS.mainFormTheme]: COLUMNS.mainFormPrimaryColor,
              [COLUMNS.altFormTheme]: COLUMNS.altFormPrimaryColor,
              [COLUMNS.mainFormEsTheme]: COLUMNS.mainFormEsPrimaryColor,
            }[column];
            return (
              <ColorCell
                key={column}
                column={column}
                bgColor={websiteData[bgColorKey] as string}
              >
                <Highlight
                  text={websiteData[column] as string}
                  highlight={filters[column]}
                />
              </ColorCell>
            );
          }

          case COLUMNS.pages:
            return (
              <PagesCell
                key={column}
                column={column}
                websiteData={websiteData}
                host={host}
                filters={filters}
              />
            );

          case COLUMNS.forms: {
            const formsData = websiteData[column] as unknown as Forms;
            const formKeys = Object.keys(formsData);
            return (
              <td
                data-title={fromCamelCaseToWords(column)}
                data-qa={column}
                key={column}
              >
                {formKeys.length ? (
                  <FormsCell
                    forms={formsData}
                    filter={filters[column]}
                    host={host}
                  />
                ) : (
                  NO_DATA
                )}
              </td>
            );
          }

          case COLUMNS.tags:
            return (
              <TagsCell
                key={column}
                column={column}
                websiteData={websiteData}
              />
            );

          default:
            return (
              <DefaultCell
                key={column}
                column={column}
                websiteData={websiteData}
                filters={filters}
              />
            );
        }
      })}
    </tr>
  );
}
